import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ZatcaAddressFields } from '@/components/settings/ZatcaAddressFields';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Save, Upload, RotateCcw, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { resolveUploadUrl } from '@/lib/uploadUrl';
import { toast } from 'sonner';

/**
 * iter774 — Company Profile = the single master record for company
 * identity (Name, Logo, CR, VAT, Address, Phone, Email, Website,
 * Country). Reads/writes the canonical branding doc (/pdf-exports/branding)
 * so Branding, ZATCA, Receipt, Invoices and Reports all read the same
 * source. We load the FULL branding object and post it back unchanged
 * apart from the identity fields, so module-specific settings (colors,
 * receipt options, language) are never wiped.
 */
export default function CompanyProfilePage() {
  const [data, setData] = useState({
    company_name: '',
    company_vat: '',
    cr_number: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    country_code: '',
    logo_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [branches, setBranches] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => { load(); loadBranches(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/pdf-exports/branding');
      setData(prev => ({ ...prev, ...res.data }));
    } catch {
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const { data: bs } = await api.get('/branches');
      setBranches(Array.isArray(bs) ? bs : []);
    } catch { setBranches([]); }
  };

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  // iter775 — Profile completeness meter. Counts the 9 identity fields.
  const COMPLETENESS_FIELDS = [
    ['company_name', 'Company name'],
    ['logo_url', 'Logo'],
    ['cr_number', 'CR number'],
    ['company_vat', 'VAT number'],
    ['company_address', 'Address'],
    ['company_phone', 'Phone'],
    ['company_email', 'Email'],
    ['company_website', 'Website'],
    ['country_code', 'Country'],
  ];
  const filled = COMPLETENESS_FIELDS.filter(([k]) => String(data[k] || '').trim());
  const missing = COMPLETENESS_FIELDS.filter(([k]) => !String(data[k] || '').trim());
  const pct = Math.round((filled.length / COMPLETENESS_FIELDS.length) * 100);
  const meterColor = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-rose-500';

  // iter776 — Branch override summary. A branch "diverges" if it sets any
  // of its own phone/address/email/VAT (otherwise it inherits the master).
  const OVERRIDE_KEYS = ['company_phone', 'company_address', 'company_email', 'company_vat'];
  const overrideBranches = branches
    .map(b => ({
      name: b.name || 'Branch',
      fields: OVERRIDE_KEYS.filter(k => String(b[k] || '').trim()),
    }))
    .filter(b => b.fields.length > 0);
  const FIELD_SHORT = { company_phone: 'phone', company_address: 'address', company_email: 'email', company_vat: 'VAT' };

  const handleSave = async () => {
    if (!String(data.company_name || '').trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      // Post the FULL object back so other branding fields are preserved.
      await api.post('/pdf-exports/branding', data);
      toast.success('Company profile saved — used everywhere automatically');
    } catch {
      toast.error('Failed to save company profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/pdf-exports/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('logo_url', res.data.logo_url);
      toast.success('Logo uploaded — click Save to apply');
    } catch {
      toast.error('Logo upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-sm text-muted-foreground" data-testid="company-profile-loading">Loading company profile…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 p-2 sm:p-4" data-testid="company-profile-page">
        <SettingsPageHeader
          icon={Building2}
          title="Company Profile"
          subtitle="The single source of truth for your company details. Branding, ZATCA, receipts, invoices and reports all read from here — enter it once."
        />

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-[12px] text-blue-900" data-testid="company-profile-note">
          <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
          <span>Changes here apply automatically across all modules. Module-specific options (theme colors, receipt layout, ZATCA certificates) stay on their own pages.</span>
        </div>

        {/* Profile completeness meter */}
        <Card data-testid="company-profile-completeness">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Profile completeness</span>
              <span className={`text-sm font-bold ${pct >= 100 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-rose-600'}`} data-testid="cp-completeness-pct">
                {filled.length}/{COMPLETENESS_FIELDS.length} · {pct}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${meterColor}`} style={{ width: `${pct}%` }} data-testid="cp-completeness-bar" />
            </div>
            {missing.length > 0 ? (
              <p className="text-[11px] text-muted-foreground mt-2" data-testid="cp-completeness-missing">
                Add {missing.map(([, l]) => l).join(', ')} to look more professional on invoices &amp; receipts.
              </p>
            ) : (
              <p className="text-[11px] text-emerald-600 mt-2 font-medium" data-testid="cp-completeness-done">
                All set — your invoices, receipts and ZATCA QR will show complete company details. ✓
              </p>
            )}
          </CardContent>
        </Card>

        {/* Branch override summary */}
        {branches.length > 0 && (
          <Card data-testid="branch-override-summary">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold">Branch overrides</div>
                <Link to="/branding" className="text-[12px] text-emerald-700 hover:underline font-medium" data-testid="branch-override-manage-link">
                  Manage per-branch →
                </Link>
              </div>
              {overrideBranches.length === 0 ? (
                <p className="text-[12px] text-muted-foreground mt-1" data-testid="branch-override-none">
                  All {branches.length} branch{branches.length > 1 ? 'es' : ''} use the company profile details above. Set a branch's own phone/address/email/VAT in Branding &amp; PDF if it differs.
                </p>
              ) : (
                <>
                  <p className="text-[12px] text-stone-700 mt-1" data-testid="branch-override-count">
                    <strong>{overrideBranches.length}</strong> of {branches.length} branch{branches.length > 1 ? 'es' : ''} use their own details (the rest inherit from this profile).
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {overrideBranches.map((b, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px]" data-testid={`branch-override-chip-${i}`}>
                        {b.name}
                        <span className="text-amber-600">· {b.fields.map(f => FIELD_SHORT[f]).join(', ')}</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Company Logo</CardTitle>
            <CardDescription>Shown on invoices, PDF exports and the app header.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-24 h-24 rounded-xl border bg-muted/40 flex items-center justify-center overflow-hidden" data-testid="company-logo-preview">
                {data.logo_url ? (
                  <img src={resolveUploadUrl(data.logo_url)} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <Building2 size={32} className="text-muted-foreground" />
                )}
              </div>
              <div className="space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="company-logo-input" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="company-logo-upload-btn">
                  <Upload size={14} className="mr-1" /> {uploading ? 'Uploading…' : 'Upload logo'}
                </Button>
                {data.logo_url && (
                  <Button variant="ghost" size="sm" onClick={() => { set('logo_url', ''); toast.success('Logo cleared — click Save to apply'); }} className="text-muted-foreground hover:text-destructive" data-testid="company-logo-reset-btn">
                    <RotateCcw size={14} className="mr-1" /> Remove
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">PNG, JPG or SVG. Max 2MB recommended.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Identity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Company Details</CardTitle>
            <CardDescription>Legal name, registration & contact information.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input id="company_name" value={data.company_name || ''} onChange={(e) => set('company_name', e.target.value)} placeholder="Your Company Name" data-testid="cp-company-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name_ar">Company Name (Arabic)</Label>
                <Input id="company_name_ar" dir="rtl" value={data.company_name_ar || ''} onChange={(e) => set('company_name_ar', e.target.value)} placeholder="اسم الشركة" data-testid="cp-company-name-ar" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zatca_seller_name">Legal seller name on e-invoices (ZATCA)</Label>
                <Input id="zatca_seller_name" value={data.zatca_seller_name || ''} onChange={(e) => set('zatca_seller_name', e.target.value)} placeholder="Blank = Company Name" data-testid="cp-zatca-seller-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr_number">Commercial Registration (CR)</Label>
                <Input id="cr_number" value={data.cr_number || ''} onChange={(e) => set('cr_number', e.target.value)} placeholder="1010xxxxxx" data-testid="cp-cr-number" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_vat">VAT Number</Label>
                <Input id="company_vat" value={data.company_vat || ''} onChange={(e) => set('company_vat', e.target.value)} placeholder="3xxxxxxxxx00003" data-testid="cp-vat-number" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="company_address">Address</Label>
                <Textarea id="company_address" value={data.company_address || ''} onChange={(e) => set('company_address', e.target.value)} placeholder="Building, Street, District, City, Country" rows={2} data-testid="cp-address" />
              </div>
              <ZatcaAddressFields data={data} set={set} />
              <div className="space-y-2">
                <Label htmlFor="company_phone">Phone</Label>
                <Input id="company_phone" value={data.company_phone || ''} onChange={(e) => set('company_phone', e.target.value)} placeholder="+966 50 000 0000" data-testid="cp-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_email">Email</Label>
                <Input id="company_email" type="email" value={data.company_email || ''} onChange={(e) => set('company_email', e.target.value)} placeholder="info@company.com" data-testid="cp-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_website">Website</Label>
                <Input id="company_website" value={data.company_website || ''} onChange={(e) => set('company_website', e.target.value)} placeholder="www.company.com" data-testid="cp-website" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country_code">Country Code</Label>
                <Input id="country_code" value={data.country_code || ''} onChange={(e) => set('country_code', e.target.value.toUpperCase().slice(0, 2))} placeholder="SA" maxLength={2} data-testid="cp-country" />
                <p className="text-[11px] text-muted-foreground">ISO 2-letter (e.g. SA, AE, IN). Drives region-specific settings.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end sticky bottom-4">
          <Button onClick={handleSave} disabled={saving} className="shadow-lg" data-testid="cp-save-btn">
            <Save size={16} className="mr-1" /> {saving ? 'Saving…' : 'Save Company Profile'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
